import { useCallback, useEffect, useRef } from 'react';

// ─── Message types (website → admin-UI) ───────────────────────────────────────

export type FromIframeMessage =
  | { type: 'READY' }
  | {
      type: 'BLOCK_SELECT';
      blockId: string;
      blockType: string;
      blockDefId: string | null;
      props: Record<string, unknown>;
    }
  | { type: 'BLOCK_REORDER'; orderedBlockIds: string[] }
  | { type: 'ADD_BLOCK_TO_COLUMN'; parentBlockId: string; column: number }
  | { type: 'ADD_BLOCK_ROOT' }
  | { type: 'BLOCK_DELETE'; blockId: string }
  | { type: 'BLOCK_MOVE'; blockId: string; direction: 'up' | 'down' };

// ─── Message types (admin-UI → website) ───────────────────────────────────────

export type ToIframeMessage =
  | { type: 'BLOCK_UPDATE'; blockId: string; props: Record<string, unknown> }
  | { type: 'BLOCK_TREE'; blocks: unknown[] }
  | { type: 'BLOCK_DELETE'; blockId: string };

interface UseBuilderMessagesOptions {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  iframeOrigin: string;
  onMessage: (msg: FromIframeMessage) => void;
}

/**
 * Manages bidirectional postMessage communication between the Visual Builder
 * and the website iframe.
 *
 * - Listens for messages from the iframe (READY, BLOCK_SELECT, BLOCK_REORDER)
 * - Exposes `sendToIframe(msg)` to send commands to the iframe
 */
export function useBuilderMessages({
  iframeRef,
  iframeOrigin,
  onMessage,
}: UseBuilderMessagesOptions) {
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Only accept messages from the iframe origin
      if (event.origin !== iframeOrigin) {
        if ((event.data as Record<string, unknown>)?.type) {
          console.log('[BuilderMessages] origin mismatch — expected:', iframeOrigin, 'got:', event.origin, 'msg:', (event.data as Record<string, unknown>).type);
        }
        return;
      }
      // Only accept messages from our specific iframe
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;

      const msg = event.data as FromIframeMessage;
      if (!msg?.type) return;
      console.log('[BuilderMessages] received:', msg.type);
      onMessageRef.current(msg);
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeOrigin, iframeRef]);

  const sendToIframe = useCallback(
    (msg: ToIframeMessage) => {
      iframeRef.current?.contentWindow?.postMessage(msg, iframeOrigin);
    },
    [iframeRef, iframeOrigin],
  );

  return { sendToIframe };
}
